import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { useAdmin } from '../context/AdminContext'
import AdminNav from '../components/AdminNav'

const BLUE   = '#1877F2'
const TEXT   = '#050505'
const MUTED  = '#65676B'
const BORDER = '#E4E6EB'
const BG     = '#F0F2F5'
const WHITE  = '#FFFFFF'
const GREEN  = '#16A34A'
const AMBER  = '#D97706'
const RED    = '#DC2626'
const PURPLE = '#7C3AED'
const GOLD   = '#F59E0B'

const PLAN_STYLE = {
  trial:     { bg: '#FFFBEB', color: AMBER  },
  starter:   { bg: BG,       color: MUTED  },
  growth:    { bg: '#EFF6FF', color: BLUE   },
  pro:       { bg: '#F5F3FF', color: PURPLE },
  business:  { bg: '#F0FDF4', color: GREEN  },
  suspended: { bg: '#FEF2F2', color: RED    },
}

const VERIFY_STYLE = {
  verified:   { bg: '#F0FDF4', color: GREEN },
  pending:    { bg: '#EFF6FF', color: BLUE  },
  unverified: { bg: BG,       color: MUTED },
}

function Icon({ path, size = 15, color = MUTED, sw = 1.8 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
      <path d={path} />
    </svg>
  )
}

const IC = {
  search:  'M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z',
  shop:    'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  orders:  'M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z',
  revenue: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  users:   'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
  back:    'M15 18l-6-6 6-6',
  ext:     'M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14',
  receipt: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  lock:    'M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636',
  unlock:  'M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z',
  plan:    'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  trial:   'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  verify:  'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  fb:      'M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z',
  studio:  'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
  plus:    'M12 4v16m8-8H4',
}

function Pill({ label, style }) {
  return (
    <span style={{ fontSize: '10.5px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px', background: style?.bg || BG, color: style?.color || MUTED, whiteSpace: 'nowrap', textTransform: 'capitalize' }}>
      {label}
    </span>
  )
}

function StatBox({ label, value, color, bg }) {
  return (
    <div style={{ background: bg, borderRadius: '10px', padding: '12px 14px', textAlign: 'center' }}>
      <div style={{ fontSize: '18px', fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}>{label}</div>
    </div>
  )
}

const PAGE_SIZE = 15

function AdminSellers() {
  const { admin } = useAdmin()

  const [sellers, setSellers]       = useState([])
  const [loading, setLoading]       = useState(true)
  const [search, setSearch]         = useState('')
  const [filterPlan, setFilterPlan] = useState('all')
  const [filterMode, setFilterMode] = useState('all')
  const [filterVerify, setFilterVerify] = useState('all')
  const [filterCountry, setFilterCountry] = useState('all')
  const [filterCategory, setFilterCategory] = useState('all')
  const [page, setPage]             = useState(1)
  const [selected, setSelected]     = useState(null)
  const [sellerStats, setSellerStats] = useState({})
  const [updating, setUpdating]     = useState(false)
  const [saved, setSaved]           = useState('')
  const [prices, setPrices]         = useState({})
  const [brand, setBrand]           = useState({})
  const [activeDetailTab, setActiveDetailTab] = useState('overview')

  // Subscription modal states
  const [showPlanModal, setShowPlanModal]       = useState(false)
  const [showTrialModal, setShowTrialModal]     = useState(false)
  const [showSuspendModal, setShowSuspendModal] = useState(false)
  const [planForm, setPlanForm]   = useState({ plan: '', type: 'monthly', months: 1, notes: '', markPaid: false, amount: '', paymentMethod: '' })
  const [newTrialDate, setNewTrialDate] = useState('')
  const [suspendReason, setSuspendReason] = useState('')
  const [sendingReceipt, setSendingReceipt] = useState(false)

  // AI Studio states
  const [aiCredits, setAiCredits]             = useState(null)
  const [aiHistory, setAiHistory]             = useState([])
  const [aiHistoryLoading, setAiHistoryLoading] = useState(false)
  const [showAddCreditsModal, setShowAddCreditsModal] = useState(false)
  const [addCreditsForm, setAddCreditsForm]   = useState({ amount: '', type: 'paid', reason: '' })
  const [addingCredits, setAddingCredits]     = useState(false)

  useEffect(() => { fetchAll() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    setLoading(true)
    const [{ data: sellersData }, { data: settingsData }] = await Promise.all([
      supabase.from('sellers').select('*').order('created_at', { ascending: false }),
      supabase.from('admin_settings').select('key,value'),
    ])
    setSellers(sellersData || [])
    const map = {}
    settingsData?.forEach(s => { map[s.key] = s.value })
    setPrices(map)
    setBrand(map)
    setLoading(false)
  }

  const fetchSellerStats = async (id) => {
    const [{ count: orders }, { count: products }, { count: customers }, { data: rev }] = await Promise.all([
      supabase.from('orders').select('*', { count: 'exact', head: true }).eq('seller_id', id),
      supabase.from('products').select('*', { count: 'exact', head: true }).eq('seller_id', id),
      supabase.from('customers').select('*', { count: 'exact', head: true }).eq('seller_id', id),
      supabase.from('orders').select('total_amount').eq('seller_id', id).neq('status', 'return'),
    ])
    const revenue = rev?.reduce((s, o) => s + Number(o.total_amount || 0), 0) || 0
    setSellerStats({ orders: orders || 0, products: products || 0, customers: customers || 0, revenue })
  }

  const fetchAiData = async (id) => {
    setAiHistoryLoading(true)
    const [{ data: credits }, { data: history }] = await Promise.all([
      supabase.from('seller_ai_credits').select('*').eq('seller_id', id).single(),
      supabase.from('ai_generation_history').select('*').eq('seller_id', id).order('created_at', { ascending: false }).limit(20),
    ])
    setAiCredits(credits || null)
    setAiHistory(history || [])
    setAiHistoryLoading(false)
  }

  const updateSeller = async (id, updates) => {
    setUpdating(true)
    await supabase.from('sellers').update(updates).eq('id', id)
    await fetchAll()
    setSelected(prev => prev?.id === id ? { ...prev, ...updates } : prev)
    setUpdating(false)
    setSaved('Saved!')
    setTimeout(() => setSaved(''), 2500)
  }

  const handleActivatePlan = async () => {
    if (!planForm.plan) return
    const start = new Date()
    const end   = new Date()
    end.setMonth(end.getMonth() + Number(planForm.months))

    await updateSeller(selected.id, {
      plan: planForm.plan, subscription_plan: planForm.plan,
      subscription_start: start.toISOString(), subscription_end: end.toISOString(),
      subscription_type: planForm.type, subscription_notes: planForm.notes || null,
      suspended: false, suspension_reason: null,
      activated_at: start.toISOString(), activated_by: admin?.name || 'Admin',
    })

    // If pro or business — reset free AI credits
    if (planForm.plan === 'pro' || planForm.plan === 'business') {
      const freeMap = { pro: 3, business: 10 }
      const freeCredits = freeMap[planForm.plan] || 0
      await supabase.from('seller_ai_credits').upsert({
        seller_id:     selected.id,
        free_credits:  freeCredits,
        free_reset_at: start.toISOString(),
      }, { onConflict: 'seller_id', ignoreDuplicates: false })
    }

    // Create payment record for audit trail if marked as manually paid
    if (planForm.markPaid && planForm.amount) {
      const isInt   = selected.seller_mode === 'international'
      const tran_id = 'MANUAL-' + (admin?.name || 'ADMIN').replace(/\s/g,'') + '-' + Date.now()
      await supabase.from('subscription_payments').insert({
        seller_id:       selected.id,
        tran_id,
        plan_key:        planForm.plan,
        plan_label:      planForm.plan.charAt(0).toUpperCase() + planForm.plan.slice(1),
        amount:          Number(planForm.amount),
        currency:        isInt ? 'usd' : 'bdt',
        billing_period:  planForm.type === 'annual' ? 'annual' : 'monthly',
        status:          'paid',
        paid_at:         start.toISOString(),
        period_start:    start.toISOString(),
        period_end:      end.toISOString(),
        gateway_response: JSON.stringify({ manual: true, note: planForm.notes, admin: admin?.name || 'Admin' }),
        created_at:      start.toISOString(),
      })

      // Send invoice email
      try {
        const { data: sellerRecord } = await supabase.from('sellers').select('email, shop_name').eq('id', selected.id).single()
        if (sellerRecord?.email) {
          await supabase.functions.invoke('send-email', {
            body: {
              type: 'purchase_invoice',
              to:   sellerRecord.email,
              data: {
                shopName:      sellerRecord.shop_name || 'PageConnect Seller',
                invoiceNumber: 'PC-M-' + tran_id.slice(-8),
                date:          start.toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: 'numeric' }),
                item:          'PageConnect ' + planForm.plan.charAt(0).toUpperCase() + planForm.plan.slice(1),
                period:        planForm.type === 'annual' ? 'Annual (12 months)' : 'Monthly',
                subtotal:      planForm.amount,
                taxPct:        0,
                tax:           '0',
                total:         planForm.amount,
                currency:      isInt ? 'usd' : 'bdt',
                paymentMethod: 'Manual — ' + (planForm.paymentMethod || 'Admin activated'),
                invoiceUrl:    'https://app.pageconnect.io/settings?tab=account',
              }
            }
          })
        }
      } catch { /* email failure never blocks */ }
    }

    setShowPlanModal(false)
  }

  const handleExtendTrial = async () => {
    if (!newTrialDate) return
    await updateSeller(selected.id, {
      plan: 'trial', subscription_plan: 'trial',
      trial_ends_at: new Date(newTrialDate).toISOString(), suspended: false,
    })
    setShowTrialModal(false)
    setNewTrialDate('')
  }

  const handleSuspend = async () => {
    await updateSeller(selected.id, {
      plan: 'suspended', subscription_plan: 'suspended',
      suspended: true, suspension_reason: suspendReason,
    })
    setShowSuspendModal(false)
    setSuspendReason('')
  }

  const handleUnsuspend = async () => {
    await updateSeller(selected.id, {
      plan: 'trial', subscription_plan: 'trial',
      suspended: false, suspension_reason: null,
    })
  }

  // ── Manual AI credits ──────────────────────────────────────────────────
  const handleAddCredits = async () => {
    if (!addCreditsForm.amount || Number(addCreditsForm.amount) < 1) return
    setAddingCredits(true)
    const amount = Number(addCreditsForm.amount)

    const existing = aiCredits
    if (existing) {
      const updates = addCreditsForm.type === 'free'
        ? { free_credits: (existing.free_credits || 0) + amount, updated_at: new Date().toISOString() }
        : { paid_credits: (existing.paid_credits || 0) + amount, updated_at: new Date().toISOString() }
      await supabase.from('seller_ai_credits').update(updates).eq('seller_id', selected.id)
    } else {
      await supabase.from('seller_ai_credits').insert({
        seller_id:    selected.id,
        free_credits: addCreditsForm.type === 'free' ? amount : 0,
        paid_credits: addCreditsForm.type === 'paid' ? amount : 0,
        free_reset_at: new Date().toISOString(),
      })
    }

    // Log in ai_credit_purchases as manual top-up for audit trail
    await supabase.from('ai_credit_purchases').insert({
      seller_id:  selected.id,
      tran_id:    'MANUAL-' + admin?.name?.replace(/\s/g, '') + '-' + Date.now(),
      pack_name:  'Manual — ' + (addCreditsForm.reason || 'Admin top-up'),
      credits:    amount,
      amount:     0,
      currency:   'bdt',
      status:     'paid',
      paid_at:    new Date().toISOString(),
      created_at: new Date().toISOString(),
    })

    await fetchAiData(selected.id)
    setAddingCredits(false)
    setShowAddCreditsModal(false)
    setAddCreditsForm({ amount: '', type: 'paid', reason: '' })
    setSaved('Credits added!')
    setTimeout(() => setSaved(''), 2500)
  }

  const handleResetFreeCredits = async () => {
    const plan = selected.subscription_plan || selected.plan || 'trial'
    const freeMap = { pro: 3, business: 10 }
    const freeCredits = freeMap[plan] || 0
    if (aiCredits) {
      await supabase.from('seller_ai_credits').update({ free_credits: freeCredits, free_reset_at: new Date().toISOString(), updated_at: new Date().toISOString() }).eq('seller_id', selected.id)
    } else {
      await supabase.from('seller_ai_credits').insert({ seller_id: selected.id, free_credits: freeCredits, paid_credits: 0, free_reset_at: new Date().toISOString() })
    }
    await fetchAiData(selected.id)
    setSaved('Free credits reset!')
    setTimeout(() => setSaved(''), 2500)
  }

  const handleSendReceipt = () => {
    setSendingReceipt(true)
    const s       = selected
    const isInt   = s.seller_mode === 'international'
    const sym     = isInt ? '$' : '৳'
    const planKey = s.subscription_plan || s.plan || 'trial'
    const planType = s.subscription_type || 'monthly'
    const planLabel = planKey.charAt(0).toUpperCase() + planKey.slice(1)
    const priceKey  = 'price_' + planKey + '_' + planType + '_' + (isInt ? 'usd' : 'bdt')
    const price     = prices[priceKey] || '—'
    const brandName = brand.brand_name || 'PageConnect'
    const brandSite = brand.brand_website || 'pageconnect.io'
    const brandEmail = brand.brand_email || 'help@pageconnect.io'
    const startDate = s.subscription_start ? new Date(s.subscription_start).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'
    const endDate   = s.subscription_end   ? new Date(s.subscription_end).toLocaleDateString('en-GB',   { day: 'numeric', month: 'long', year: 'numeric' }) : '—'
    const subject = brandName + ' — Subscription Receipt — ' + planLabel + ' Plan'
    const body = ['Dear ' + (s.owner_name || s.shop_name) + ',', '', 'Thank you for subscribing to ' + brandName + '!', '', '--- SUBSCRIPTION RECEIPT ---', '', 'Shop: ' + s.shop_name, 'Plan: ' + planLabel, 'Billing: ' + planType.charAt(0).toUpperCase() + planType.slice(1), 'Amount: ' + sym + price + ' / ' + planType, 'Start: ' + startDate, 'Next renewal: ' + endDate, '', 'Receipt: RCP-' + Date.now(), '', 'Support: ' + brandEmail, 'Website: ' + brandSite, '', 'Thank you for trusting ' + brandName + '.', '', brandName + ' Team'].join('\n')
    window.open('mailto:' + s.email + '?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(body))
    setSendingReceipt(false)
  }

  const fmt = (n) => {
    const v = Math.abs(Math.round(n || 0))
    if (v >= 1000000) return '৳' + (v / 1000000).toFixed(1) + 'M'
    if (v >= 100000)  return '৳' + Math.round(v / 1000) + 'k'
    return '৳' + v.toLocaleString()
  }

  const trialDaysLeft = (s) => {
    const d = s?.trial_ends_at || s?.subscription_end
    if (!d) return null
    return Math.ceil((new Date(d) - new Date()) / 86400000)
  }

  const getPlanLabel = (s) => {
    if (s.suspended) return 'Suspended'
    const plan = s.subscription_plan || s.plan || 'trial'
    if (plan === 'trial') {
      const days = trialDaysLeft(s)
      if (days !== null && days <= 0) return 'Trial expired'
      return 'Trial' + (days !== null ? ' (' + days + 'd)' : '')
    }
    return plan
  }

  const countries   = [...new Set(sellers.map(s => s.country).filter(Boolean))].sort()
  const categories  = [...new Set(sellers.map(s => s.category).filter(Boolean))].sort()

  const planCounts = {}
  sellers.forEach(s => {
    const p = s.suspended ? 'suspended' : (s.subscription_plan || s.plan || 'trial')
    planCounts[p] = (planCounts[p] || 0) + 1
  })

  const countryCounts = {}
  sellers.forEach(s => {
    const c = s.country || (s.seller_mode === 'bangladesh' ? 'Bangladesh' : 'Unknown')
    countryCounts[c] = (countryCounts[c] || 0) + 1
  })

  const filtered = sellers.filter(s => {
    const q = search.toLowerCase()
    const matchSearch = !search || s.shop_name?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q) || s.phone?.includes(search)
    const plan = s.suspended ? 'suspended' : (s.subscription_plan || s.plan || 'trial')
    const matchPlan    = filterPlan === 'all' || plan === filterPlan
    const matchMode    = filterMode === 'all' || (filterMode === 'bangladesh' ? s.seller_mode !== 'international' : s.seller_mode === 'international')
    const matchVerify  = filterVerify === 'all' || s.verification_status === filterVerify
    const matchCountry = filterCountry === 'all' || s.country === filterCountry
    const matchCat     = filterCategory === 'all' || s.category === filterCategory
    return matchSearch && matchPlan && matchMode && matchVerify && matchCountry && matchCat
  })

  const totalPages  = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const pageSellers = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  const currentPlan = selected ? (selected.suspended ? 'suspended' : (selected.subscription_plan || selected.plan || 'trial')) : ''
  const inp = { border: '1px solid ' + BORDER, borderRadius: '9px', padding: '7px 11px', fontSize: '12px', outline: 'none', background: WHITE, color: TEXT, fontFamily: 'Space Grotesk, sans-serif', width: '100%', boxSizing: 'border-box' }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: BG, fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
      <AdminNav />
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <div style={{ padding: '24px 28px' }}>
          <style>{`@media(max-width:960px){ .sellers-layout{flex-direction:column!important} .sellers-list{width:100%!important; max-height:400px} }`}</style>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 800, color: TEXT, letterSpacing: '-0.3px' }}>Sellers</div>
              <div style={{ fontSize: '12.5px', color: MUTED, marginTop: '2px' }}>{sellers.length} total sellers registered</div>
            </div>
            <button onClick={fetchAll} style={{ fontSize: '12px', fontWeight: 600, color: MUTED, background: WHITE, border: '1px solid ' + BORDER, padding: '7px 14px', borderRadius: '9px', cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>↻ Refresh</button>
          </div>

          {/* Plan breakdown strip */}
          {!loading && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '8px', marginBottom: '16px' }}>
              {['trial','starter','growth','pro','business','suspended'].map(plan => (
                <div key={plan} onClick={() => { setFilterPlan(filterPlan === plan ? 'all' : plan); setPage(1) }}
                  style={{ background: filterPlan === plan ? (PLAN_STYLE[plan]?.bg || BG) : WHITE, border: '1px solid ' + (filterPlan === plan ? (PLAN_STYLE[plan]?.color || BORDER) : BORDER), borderRadius: '10px', padding: '10px 8px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.12s' }}>
                  <div style={{ fontSize: '18px', fontWeight: 800, color: PLAN_STYLE[plan]?.color || MUTED }}>{planCounts[plan] || 0}</div>
                  <div style={{ fontSize: '10px', color: MUTED, marginTop: '2px', textTransform: 'capitalize' }}>{plan}</div>
                </div>
              ))}
              <div style={{ background: WHITE, border: '1px solid ' + BORDER, borderRadius: '10px', padding: '10px 8px', textAlign: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: 800, color: TEXT }}>{sellers.length}</div>
                <div style={{ fontSize: '10px', color: MUTED, marginTop: '2px' }}>Total</div>
              </div>
            </div>
          )}

          {loading && <div style={{ textAlign: 'center', padding: '60px', color: MUTED, fontSize: '13px' }}>Loading sellers...</div>}

          {!loading && (
            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }} className="sellers-layout">

              {/* ── LEFT: List + filters ── */}
              <div style={{ width: '340px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '10px' }} className="sellers-list">
                <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '14px' }}>
                  <div style={{ position: 'relative', marginBottom: '10px' }}>
                    <input type="text" placeholder="Search name, email, phone..." value={search}
                      onChange={e => { setSearch(e.target.value); setPage(1) }}
                      style={{ ...inp, paddingLeft: '32px' }}
                      onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
                    <div style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }}><Icon path={IC.search} color={MUTED} size={13} /></div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '6px' }}>
                    <select value={filterPlan} onChange={e => { setFilterPlan(e.target.value); setPage(1) }} style={inp} onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER}>
                      <option value="all">All plans</option>
                      {['trial','starter','growth','pro','business','suspended'].map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                    </select>
                    <select value={filterMode} onChange={e => { setFilterMode(e.target.value); setPage(1) }} style={inp} onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER}>
                      <option value="all">All regions</option>
                      <option value="bangladesh">Bangladesh</option>
                      <option value="international">International</option>
                    </select>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '6px' }}>
                    <select value={filterVerify} onChange={e => { setFilterVerify(e.target.value); setPage(1) }} style={inp} onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER}>
                      <option value="all">All verification</option>
                      <option value="verified">Verified</option>
                      <option value="pending">Pending</option>
                      <option value="unverified">Unverified</option>
                    </select>
                    <select value={filterCountry} onChange={e => { setFilterCountry(e.target.value); setPage(1) }} style={inp} onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER}>
                      <option value="all">All countries</option>
                      {countries.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <select value={filterCategory} onChange={e => { setFilterCategory(e.target.value); setPage(1) }} style={inp} onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER}>
                    <option value="all">All categories</option>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <div style={{ fontSize: '11px', color: MUTED, marginTop: '8px' }}>Showing <strong style={{ color: TEXT }}>{filtered.length}</strong> of {sellers.length} sellers</div>
                </div>

                <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, overflow: 'hidden' }}>
                  {pageSellers.map((s, i) => {
                    const plan = s.suspended ? 'suspended' : (s.subscription_plan || s.plan || 'trial')
                    const ps   = PLAN_STYLE[plan] || PLAN_STYLE.trial
                    const vs   = VERIFY_STYLE[s.verification_status] || VERIFY_STYLE.unverified
                    const isSelected = selected?.id === s.id
                    return (
                      <div key={s.id}
                        onClick={() => { setSelected(s); fetchSellerStats(s.id); setActiveDetailTab('overview'); setAiCredits(null); setAiHistory([]) }}
                        style={{ padding: '11px 14px', borderBottom: i < pageSellers.length - 1 ? '1px solid #F5F5F5' : 'none', cursor: 'pointer', background: isSelected ? '#EFF6FF' : WHITE, borderLeft: isSelected ? '3px solid ' + BLUE : '3px solid transparent', transition: 'all 0.1s' }}
                        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = BG }}
                        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = WHITE }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '3px' }}>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, marginRight: '8px' }}>
                            {s.shop_name}{s.seller_mode === 'international' && <span style={{ marginLeft: '4px' }}>🌍</span>}
                          </div>
                          <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '20px', background: ps.bg, color: ps.color, flexShrink: 0 }}>{getPlanLabel(s)}</span>
                        </div>
                        <div style={{ fontSize: '11px', color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '2px' }}>{s.email}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '10px', color: MUTED }}>{new Date(s.created_at).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: '2-digit' })}</span>
                          {s.verification_status && s.verification_status !== 'unverified' && (
                            <span style={{ fontSize: '10px', fontWeight: 600, padding: '1px 6px', borderRadius: '10px', background: vs.bg, color: vs.color }}>{s.verification_status}</span>
                          )}
                          {s.category && <span style={{ fontSize: '10px', color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.category}</span>}
                        </div>
                      </div>
                    )
                  })}
                  {filtered.length === 0 && <div style={{ padding: '32px', textAlign: 'center', color: MUTED, fontSize: '12px' }}>No sellers match filters</div>}
                  {totalPages > 1 && (
                    <div style={{ padding: '8px 14px', borderTop: '1px solid ' + BORDER, background: BG, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '11px', color: MUTED }}>{page}/{totalPages}</span>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ width: '26px', height: '26px', borderRadius: '6px', border: '1px solid ' + BORDER, background: WHITE, cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, fontSize: '13px' }}>‹</button>
                        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ width: '26px', height: '26px', borderRadius: '6px', border: '1px solid ' + BORDER, background: WHITE, cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.4 : 1, fontSize: '13px' }}>›</button>
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '14px' }}>
                  <div style={{ fontSize: '11.5px', fontWeight: 700, color: TEXT, marginBottom: '10px' }}>Sellers by country</div>
                  {Object.entries(countryCounts).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([country, count]) => (
                    <div key={country} onClick={() => { setFilterCountry(filterCountry === country ? 'all' : country); setPage(1) }}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px', cursor: 'pointer' }}>
                      <span style={{ fontSize: '12px', color: filterCountry === country ? BLUE : TEXT, fontWeight: filterCountry === country ? 700 : 400 }}>{country}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '60px', height: '5px', background: BG, borderRadius: '3px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: ((count / sellers.length) * 100) + '%', background: BLUE, borderRadius: '3px' }} />
                        </div>
                        <span style={{ fontSize: '11px', fontWeight: 700, color: MUTED, width: '20px', textAlign: 'right' }}>{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── RIGHT: Detail panel ── */}
              {selected && (
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>

                  {/* Header */}
                  <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '18px', fontWeight: 800, color: TEXT, marginBottom: '2px' }}>
                          {selected.shop_name}{selected.seller_mode === 'international' && <span style={{ marginLeft: '6px' }}>🌍</span>}
                        </div>
                        <div style={{ fontSize: '12px', color: MUTED, marginBottom: '8px' }}>{selected.email}</div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                          <Pill label={getPlanLabel(selected)} style={PLAN_STYLE[currentPlan]} />
                          <Pill label={selected.verification_status || 'unverified'} style={VERIFY_STYLE[selected.verification_status]} />
                          {selected.category && <Pill label={selected.category} style={{ bg: BG, color: MUTED }} />}
                          {saved && <span style={{ fontSize: '12px', fontWeight: 600, color: GREEN }}>{saved}</span>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                        {selected.facebook_url && (
                          <button onClick={() => window.open(selected.facebook_url, '_blank')}
                            style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, color: BLUE, background: '#EFF6FF', border: 'none', padding: '7px 12px', borderRadius: '8px', cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
                            <Icon path={IC.fb} color={BLUE} size={13} sw={2} /> Page
                          </button>
                        )}
                        <button onClick={() => setSelected(null)}
                          style={{ fontSize: '12px', fontWeight: 600, color: MUTED, background: BG, border: '1px solid ' + BORDER, padding: '7px 12px', borderRadius: '8px', cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
                          ✕ Close
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Stats */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px' }}>
                    <StatBox label="Orders"    value={sellerStats.orders    || 0}    color={BLUE}   bg="#EFF6FF" />
                    <StatBox label="Products"  value={sellerStats.products  || 0}    color={GREEN}  bg="#F0FDF4" />
                    <StatBox label="Customers" value={sellerStats.customers || 0}    color={PURPLE} bg="#F5F3FF" />
                    <StatBox label="Revenue"   value={fmt(sellerStats.revenue || 0)} color={AMBER}  bg="#FFFBEB" />
                  </div>

                  {/* Tabs — added AI Studio */}
                  <div style={{ display: 'flex', gap: '4px', padding: '4px', background: WHITE, border: '1px solid ' + BORDER, borderRadius: '10px', width: 'fit-content', overflowX: 'auto' }}>
                    {['overview','subscription','ai studio','account','verification'].map(tab => (
                      <button key={tab} onClick={() => { setActiveDetailTab(tab); if (tab === 'ai studio') fetchAiData(selected.id) }}
                        style={{ padding: '6px 14px', borderRadius: '7px', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer', background: activeDetailTab === tab ? BLUE : 'transparent', color: activeDetailTab === tab ? WHITE : MUTED, fontFamily: 'Space Grotesk, sans-serif', textTransform: 'capitalize', transition: 'all 0.12s', whiteSpace: 'nowrap' }}>
                        {tab === 'ai studio' ? '🎬 AI Studio' : tab}
                      </button>
                    ))}
                  </div>

                  {/* OVERVIEW TAB */}
                  {activeDetailTab === 'overview' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px 18px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '12px' }}>Account info</div>
                        {[
                          { label: 'Phone',       value: selected.phone },
                          { label: 'Category',    value: selected.category },
                          { label: 'Region',      value: selected.seller_mode === 'international' ? 'International' : 'Bangladesh' },
                          { label: 'Country',     value: selected.country },
                          { label: 'District',    value: selected.district },
                          { label: 'Currency',    value: selected.seller_currency },
                          { label: 'Setup done',  value: selected.setup_completed ? 'Yes' : 'No' },
                          { label: 'Joined',      value: new Date(selected.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) },
                        ].map(({ label, value }) => value && (
                          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <span style={{ fontSize: '12px', color: MUTED }}>{label}</span>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: TEXT }}>{value}</span>
                          </div>
                        ))}
                      </div>
                      <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px 18px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '12px' }}>Owner info</div>
                        {[
                          { label: 'Owner name',    value: selected.owner_name },
                          { label: 'Owner email',   value: selected.owner_email },
                          { label: 'Owner phone',   value: selected.owner_phone },
                          { label: 'NID/Passport',  value: selected.owner_nid || selected.nid_number },
                          { label: 'Trade license', value: selected.trade_license },
                          { label: 'TIN',           value: selected.company_etin || selected.tin_number },
                          { label: 'Address',       value: selected.owner_address },
                        ].map(({ label, value }) => value && (
                          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', gap: '10px' }}>
                            <span style={{ fontSize: '12px', color: MUTED, flexShrink: 0 }}>{label}</span>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: TEXT, textAlign: 'right', wordBreak: 'break-word' }}>{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* SUBSCRIPTION TAB */}
                  {activeDetailTab === 'subscription' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px 18px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '12px' }}>Current subscription</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                          <div>
                            <div style={{ fontSize: '11px', color: MUTED, marginBottom: '4px' }}>Plan</div>
                            <Pill label={getPlanLabel(selected)} style={PLAN_STYLE[currentPlan]} />
                            {selected.subscription_type && <div style={{ fontSize: '11px', color: MUTED, marginTop: '4px', textTransform: 'capitalize' }}>{selected.subscription_type} billing</div>}
                          </div>
                          <div>
                            <div style={{ fontSize: '11px', color: MUTED, marginBottom: '4px' }}>Dates</div>
                            {selected.subscription_start && <div style={{ fontSize: '12px', color: TEXT }}>Start: {new Date(selected.subscription_start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>}
                            {selected.subscription_end && (
                              <div style={{ fontSize: '12px', color: new Date(selected.subscription_end) < new Date() ? RED : GREEN }}>
                                Expires: {new Date(selected.subscription_end).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </div>
                            )}
                            {selected.trial_ends_at && currentPlan === 'trial' && (
                              <div style={{ fontSize: '12px', color: trialDaysLeft(selected) <= 0 ? RED : AMBER }}>
                                Trial ends: {new Date(selected.trial_ends_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </div>
                            )}
                          </div>
                        </div>
                        {selected.subscription_notes && <div style={{ marginTop: '12px', background: BG, borderRadius: '8px', padding: '10px 12px', fontSize: '12px', color: TEXT }}>Note: {selected.subscription_notes}</div>}
                        {selected.suspension_reason && <div style={{ marginTop: '8px', background: '#FEF2F2', borderRadius: '8px', padding: '10px 12px', fontSize: '12px', color: RED }}>Suspended: {selected.suspension_reason}</div>}
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <button onClick={() => { setPlanForm({ plan: currentPlan, type: 'monthly', months: 1, notes: '' }); setShowPlanModal(true) }}
                          style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px', borderRadius: '10px', background: '#EFF6FF', border: '1px solid #BFDBFE', cursor: 'pointer', textAlign: 'left', fontFamily: 'Space Grotesk, sans-serif' }}>
                          <Icon path={IC.plan} color={BLUE} size={16} sw={2} />
                          <div><div style={{ fontSize: '12.5px', fontWeight: 700, color: BLUE }}>Assign plan</div><div style={{ fontSize: '11px', color: MUTED }}>Set plan & duration</div></div>
                        </button>
                        <button onClick={() => setShowTrialModal(true)}
                          style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px', borderRadius: '10px', background: '#FFFBEB', border: '1px solid #FDE68A', cursor: 'pointer', textAlign: 'left', fontFamily: 'Space Grotesk, sans-serif' }}>
                          <Icon path={IC.trial} color={AMBER} size={16} sw={2} />
                          <div><div style={{ fontSize: '12.5px', fontWeight: 700, color: AMBER }}>Extend trial</div><div style={{ fontSize: '11px', color: MUTED }}>Set new end date</div></div>
                        </button>
                        <button onClick={handleSendReceipt} disabled={sendingReceipt || currentPlan === 'trial' || currentPlan === 'suspended'}
                          style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px', borderRadius: '10px', background: '#F0FDF4', border: '1px solid #BBF7D0', cursor: 'pointer', textAlign: 'left', fontFamily: 'Space Grotesk, sans-serif', opacity: (currentPlan === 'trial' || currentPlan === 'suspended') ? 0.4 : 1 }}>
                          <Icon path={IC.receipt} color={GREEN} size={16} sw={2} />
                          <div><div style={{ fontSize: '12.5px', fontWeight: 700, color: GREEN }}>Send receipt</div><div style={{ fontSize: '11px', color: MUTED }}>Email subscription receipt</div></div>
                        </button>
                        {selected.suspended ? (
                          <button onClick={handleUnsuspend} disabled={updating}
                            style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px', borderRadius: '10px', background: '#EFF6FF', border: '1px solid #BFDBFE', cursor: 'pointer', textAlign: 'left', fontFamily: 'Space Grotesk, sans-serif' }}>
                            <Icon path={IC.unlock} color={BLUE} size={16} sw={2} />
                            <div><div style={{ fontSize: '12.5px', fontWeight: 700, color: BLUE }}>Unsuspend</div><div style={{ fontSize: '11px', color: MUTED }}>Restore trial access</div></div>
                          </button>
                        ) : (
                          <button onClick={() => setShowSuspendModal(true)} disabled={updating}
                            style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px', borderRadius: '10px', background: '#FEF2F2', border: '1px solid #FECACA', cursor: 'pointer', textAlign: 'left', fontFamily: 'Space Grotesk, sans-serif' }}>
                            <Icon path={IC.lock} color={RED} size={16} sw={2} />
                            <div><div style={{ fontSize: '12.5px', fontWeight: 700, color: RED }}>Suspend</div><div style={{ fontSize: '11px', color: MUTED }}>Block all access</div></div>
                          </button>
                        )}
                      </div>

                      <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px 18px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '12px' }}>Price reference</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '8px' }}>
                          {['starter','growth','pro','business'].map(plan => {
                            const isInt = selected.seller_mode === 'international'
                            const mKey  = 'price_' + plan + '_monthly_' + (isInt ? 'usd' : 'bdt')
                            const aKey  = 'price_' + plan + '_annual_' + (isInt ? 'usd' : 'bdt')
                            const sym   = isInt ? '$' : '৳'
                            return (
                              <div key={plan} style={{ background: BG, borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                                <div style={{ fontSize: '11px', fontWeight: 700, color: PLAN_STYLE[plan]?.color || MUTED, textTransform: 'capitalize', marginBottom: '4px' }}>{plan}</div>
                                <div style={{ fontSize: '11px', color: TEXT }}>{sym}{prices[mKey] || '—'}/mo</div>
                                <div style={{ fontSize: '10px', color: MUTED }}>{sym}{prices[aKey] || '—'}/yr</div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* AI STUDIO TAB */}
                  {activeDetailTab === 'ai studio' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

                      {/* Credits overview */}
                      <div style={{ background: '#0B1F3A', borderRadius: '12px', padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '14px' }}>
                        <div>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: '#2dd4b0', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '6px' }}>🎬 AI Studio Credits</div>
                          {aiHistoryLoading ? (
                            <div style={{ fontSize: '28px', fontWeight: 900, color: WHITE }}>—</div>
                          ) : aiCredits ? (
                            <>
                              <div style={{ fontSize: '32px', fontWeight: 900, color: WHITE, lineHeight: 1 }}>{(aiCredits.free_credits || 0) + (aiCredits.paid_credits || 0)}</div>
                              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
                                {aiCredits.free_credits || 0} free · {aiCredits.paid_credits || 0} purchased
                              </div>
                              {aiCredits.free_reset_at && (
                                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginTop: '2px' }}>
                                  Free credits last reset: {new Date(aiCredits.free_reset_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </div>
                              )}
                            </>
                          ) : (
                            <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)' }}>No credit record — seller hasn't used AI Studio yet</div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button onClick={() => setShowAddCreditsModal(true)}
                            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', borderRadius: '9px', background: GOLD, border: 'none', fontSize: '12.5px', fontWeight: 700, color: '#0B1F3A', cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
                            <Icon path={IC.plus} color="#0B1F3A" size={14} sw={2.5} />
                            Add credits
                          </button>
                          <button onClick={handleResetFreeCredits}
                            style={{ padding: '10px 16px', borderRadius: '9px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', fontSize: '12.5px', fontWeight: 700, color: WHITE, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
                            ↺ Reset free credits
                          </button>
                        </div>
                      </div>

                      {/* Info */}
                      <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px', padding: '12px 14px', fontSize: '12px', color: AMBER, lineHeight: 1.7 }}>
                        <strong>Manual credit rules:</strong> Use "Add credits" for international seller payments, IPN failures, or goodwill credits. Always note the reason. "Reset free credits" restores the seller's monthly free allowance based on their current plan ({currentPlan === 'pro' ? '3 credits' : currentPlan === 'business' ? '10 credits' : '0 credits for this plan'}).
                      </div>

                      {/* Generation history */}
                      <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, overflow: 'hidden' }}>
                        <div style={{ padding: '12px 18px', borderBottom: '1px solid ' + BORDER, background: BG, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ fontSize: '12px', fontWeight: 700, color: TEXT }}>Generation history</div>
                          <div style={{ fontSize: '11px', color: MUTED }}>{aiHistory.length} generations</div>
                        </div>
                        {aiHistoryLoading ? (
                          <div style={{ padding: '24px', textAlign: 'center', fontSize: '12px', color: MUTED }}>Loading...</div>
                        ) : aiHistory.length === 0 ? (
                          <div style={{ padding: '24px', textAlign: 'center', fontSize: '12px', color: MUTED }}>No generations yet</div>
                        ) : (
                          <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                            {aiHistory.map((gen, i) => (
                              <div key={gen.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 18px', borderBottom: i < aiHistory.length - 1 ? '1px solid #F9F9F9' : 'none' }}>
                                {gen.output_image_url ? (
                                  <img src={gen.output_image_url} alt="gen" style={{ width: '40px', height: '54px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} />
                                ) : (
                                  <div style={{ width: '40px', height: '54px', background: BG, borderRadius: '6px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>
                                    {gen.status === 'failed' ? '❌' : gen.status === 'processing' ? '⏳' : '🎬'}
                                  </div>
                                )}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: '12px', fontWeight: 600, color: TEXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{gen.category || 'Generation'}</div>
                                  <div style={{ fontSize: '11px', color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: '2px' }}>{gen.prompt?.slice(0, 60)}...</div>
                                  <div style={{ fontSize: '10.5px', color: MUTED, marginTop: '2px' }}>{new Date(gen.created_at).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                                </div>
                                <div style={{ flexShrink: 0, textAlign: 'right' }}>
                                  <span style={{ fontSize: '10.5px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px', background: gen.status === 'completed' ? '#ECFDF5' : gen.status === 'failed' ? '#FEF2F2' : '#FFFBEB', color: gen.status === 'completed' ? GREEN : gen.status === 'failed' ? RED : AMBER }}>
                                    {gen.status}
                                  </span>
                                  <div style={{ fontSize: '10.5px', color: MUTED, marginTop: '3px' }}>{gen.credits_used} credit{gen.credits_used !== 1 ? 's' : ''}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ACCOUNT TAB */}
                  {activeDetailTab === 'account' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px 18px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '12px' }}>Social & links</div>
                        {[
                          { label: 'Facebook page', value: selected.facebook_url, link: true },
                          { label: 'Instagram',     value: selected.instagram_url, link: true },
                          { label: 'TikTok',        value: selected.tiktok_url, link: true },
                          { label: 'Website',       value: selected.website_url, link: true },
                          { label: 'WhatsApp',      value: selected.whatsapp_number },
                        ].map(({ label, value, link }) => value && (
                          <div key={label} style={{ marginBottom: '8px' }}>
                            <div style={{ fontSize: '11px', color: MUTED, marginBottom: '1px' }}>{label}</div>
                            {link ? <a href={value} target="_blank" rel="noreferrer" style={{ fontSize: '12px', color: BLUE, textDecoration: 'none', wordBreak: 'break-all' }}>{value}</a> : <div style={{ fontSize: '12px', color: TEXT }}>{value}</div>}
                          </div>
                        ))}
                      </div>
                      <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px 18px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '12px' }}>Business registration</div>
                        {[
                          { label: 'Trade license',  value: selected.trade_license },
                          { label: 'eTIN',           value: selected.company_etin },
                          { label: 'BIN',            value: selected.bin_number },
                          { label: 'VAT reg.',       value: selected.vat_reg_number },
                          { label: 'ABN (AU)',       value: selected.abn_number },
                          { label: 'ACN (AU)',       value: selected.acn_number },
                          { label: 'Intl reg. no.',  value: selected.intl_reg_number },
                          { label: 'Shop address',   value: selected.shop_address },
                        ].map(({ label, value }) => value && (
                          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '7px', gap: '10px' }}>
                            <span style={{ fontSize: '12px', color: MUTED, flexShrink: 0 }}>{label}</span>
                            <span style={{ fontSize: '12px', color: TEXT, fontFamily: 'JetBrains Mono, monospace', textAlign: 'right', wordBreak: 'break-all' }}>{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* VERIFICATION TAB */}
                  {activeDetailTab === 'verification' && (
                    <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px 18px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '14px' }}>Verification control</div>
                      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                        {['verified', 'pending', 'unverified'].map(status => {
                          const vs = VERIFY_STYLE[status]
                          const isActive = selected.verification_status === status
                          return (
                            <button key={status} onClick={() => updateSeller(selected.id, { verification_status: status })}
                              disabled={updating || isActive}
                              style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '2px solid ' + (isActive ? vs.color : BORDER), background: isActive ? vs.bg : WHITE, fontSize: '13px', fontWeight: 700, color: isActive ? vs.color : MUTED, cursor: isActive ? 'default' : 'pointer', fontFamily: 'Space Grotesk, sans-serif', textTransform: 'capitalize' }}>
                              {status === 'verified' ? '✅ ' : status === 'pending' ? '⏳ ' : '✗ '}{status}
                            </button>
                          )
                        })}
                      </div>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '10px' }}>Submitted documents</div>
                      {[
                        { label: 'Owner name',    value: selected.owner_name },
                        { label: 'NID/Passport',  value: selected.owner_nid || selected.nid_number },
                        { label: 'Owner address', value: selected.owner_address },
                        { label: 'Trade license', value: selected.trade_license },
                        { label: 'Owner Facebook', value: selected.owner_facebook_url },
                      ].map(({ label, value }) => (
                        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', padding: '9px 12px', background: value ? '#F0FDF4' : BG, borderRadius: '8px' }}>
                          <span style={{ fontSize: '12px', color: MUTED }}>{label}</span>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: value ? GREEN : MUTED }}>{value || 'Not provided'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {!selected && (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, minHeight: '300px' }}>
                  <div style={{ textAlign: 'center', color: MUTED }}>
                    <div style={{ fontSize: '32px', marginBottom: '10px' }}>🏪</div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: TEXT, marginBottom: '4px' }}>Select a seller</div>
                    <div style={{ fontSize: '12px' }}>Click any seller from the list to view details</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── ASSIGN PLAN MODAL ── */}
      {showPlanModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '16px' }}>
          <div style={{ background: WHITE, borderRadius: '16px', padding: '24px', maxWidth: '420px', width: '100%', fontFamily: 'Space Grotesk, sans-serif' }}>
            <div style={{ fontSize: '15px', fontWeight: 800, color: TEXT, marginBottom: '16px' }}>Assign Subscription Plan</div>
            <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px', padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: AMBER, lineHeight: 1.6 }}>
              Use this for international sellers who paid via bank/card outside SSLCommerz, or for IPN failures. Always note the reason.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={{ fontSize: '11.5px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>Plan</label>
                <select value={planForm.plan} onChange={e => setPlanForm(f => ({ ...f, plan: e.target.value }))} style={{ ...inp, border: '1px solid ' + BORDER }} onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER}>
                  <option value="">Select plan</option>
                  {['starter','growth','pro','business'].map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: '11.5px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>Billing type</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {['monthly','annual'].map(type => (
                    <button key={type} onClick={() => setPlanForm(f => ({ ...f, type, months: type === 'annual' ? 12 : 1 }))}
                      style={{ flex: 1, padding: '9px', borderRadius: '9px', border: '2px solid ' + (planForm.type === type ? BLUE : BORDER), background: planForm.type === type ? '#EFF6FF' : WHITE, fontSize: '12.5px', fontWeight: 700, color: planForm.type === type ? BLUE : MUTED, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
                      {type === 'monthly' ? 'Monthly' : 'Annual (12mo)'}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ fontSize: '11.5px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>Duration (months)</label>
                <input type="number" value={planForm.months} onChange={e => setPlanForm(f => ({ ...f, months: e.target.value }))} min="1" max="24" style={{ ...inp, border: '1px solid ' + BORDER }} onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
                <div style={{ fontSize: '11px', color: MUTED, marginTop: '4px' }}>
                  Expires: {(() => { const d = new Date(); d.setMonth(d.getMonth() + Number(planForm.months || 1)); return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) })()}
                </div>
              </div>
              {planForm.plan && (
                <div style={{ background: BG, borderRadius: '10px', padding: '12px 14px' }}>
                  <div style={{ fontSize: '11px', color: MUTED, marginBottom: '6px' }}>Price reference</div>
                  {['bdt','usd'].map(cur => {
                    const key = 'price_' + planForm.plan + '_' + planForm.type + '_' + cur
                    return (
                      <div key={cur} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ fontSize: '12px', color: MUTED }}>{cur.toUpperCase()}</span>
                        <span style={{ fontSize: '12px', fontWeight: 700, color: TEXT }}>{cur === 'bdt' ? '৳' : '$'}{prices[key] || '—'}/mo</span>
                      </div>
                    )
                  })}
                </div>
              )}
              <div>
                <label style={{ fontSize: '11.5px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>Internal notes <span style={{ fontWeight: 400, color: '#B0B3B8' }}>required</span></label>
                <textarea value={planForm.notes} onChange={e => setPlanForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. Paid via bKash manually, ref #12345 — Hamza 11 Jun 2026" rows={2} style={{ ...inp, resize: 'none', border: '1px solid ' + BORDER }} onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
              </div>

              {/* Mark as manually paid */}
              <div style={{ background: planForm.markPaid ? '#F0FDF4' : BG, borderRadius: '10px', padding: '14px', border: '1px solid ' + (planForm.markPaid ? '#BBF7D0' : BORDER) }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: planForm.markPaid ? '12px' : 0 }}>
                  <input type="checkbox" checked={planForm.markPaid} onChange={e => setPlanForm(f => ({ ...f, markPaid: e.target.checked }))} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                  <div>
                    <div style={{ fontSize: '12.5px', fontWeight: 700, color: planForm.markPaid ? GREEN : TEXT }}>Mark as manually paid</div>
                    <div style={{ fontSize: '11px', color: MUTED }}>Creates a payment record and sends invoice email to seller</div>
                  </div>
                </label>
                {planForm.markPaid && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '4px' }}>
                    <div>
                      <label style={{ fontSize: '11px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '4px' }}>Amount paid</label>
                      <input type="number" value={planForm.amount} onChange={e => setPlanForm(f => ({ ...f, amount: e.target.value }))} placeholder="e.g. 1999" style={{ ...inp, border: '1px solid ' + BORDER }} onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '4px' }}>Payment method</label>
                      <select value={planForm.paymentMethod} onChange={e => setPlanForm(f => ({ ...f, paymentMethod: e.target.value }))} style={{ ...inp, border: '1px solid ' + BORDER }} onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER}>
                        <option value="">Select...</option>
                        {['bKash','Nagad','Rocket','Bank transfer','Wise','PayPal','Cash','Visa/Mastercard','Other'].map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowPlanModal(false)} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid ' + BORDER, background: WHITE, fontSize: '13px', fontWeight: 600, color: MUTED, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>Cancel</button>
              <button onClick={handleActivatePlan} disabled={!planForm.plan || updating}
                style={{ flex: 1, padding: '12px', borderRadius: '10px', background: !planForm.plan ? BORDER : BLUE, border: 'none', fontSize: '13px', fontWeight: 700, color: WHITE, cursor: planForm.plan ? 'pointer' : 'not-allowed', fontFamily: 'Space Grotesk, sans-serif' }}>
                {updating ? 'Saving...' : planForm.markPaid ? 'Activate + create invoice' : 'Activate plan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── EXTEND TRIAL MODAL ── */}
      {showTrialModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '16px' }}>
          <div style={{ background: WHITE, borderRadius: '16px', padding: '24px', maxWidth: '360px', width: '100%', fontFamily: 'Space Grotesk, sans-serif' }}>
            <div style={{ fontSize: '15px', fontWeight: 800, color: TEXT, marginBottom: '16px' }}>Extend / Reset Trial</div>
            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '11.5px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>New trial end date</label>
              <input type="date" value={newTrialDate} onChange={e => setNewTrialDate(e.target.value)} min={new Date().toISOString().split('T')[0]} style={{ ...inp, border: '1px solid ' + BORDER }} onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
            </div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              {[7,14,30].map(days => (
                <button key={days} onClick={() => { const d = new Date(); d.setDate(d.getDate() + days); setNewTrialDate(d.toISOString().split('T')[0]) }}
                  style={{ flex: 1, padding: '8px', borderRadius: '8px', border: '1px solid ' + BORDER, background: WHITE, fontSize: '12px', fontWeight: 600, color: AMBER, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
                  +{days}d
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => { setShowTrialModal(false); setNewTrialDate('') }} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid ' + BORDER, background: WHITE, fontSize: '13px', fontWeight: 600, color: MUTED, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>Cancel</button>
              <button onClick={handleExtendTrial} disabled={!newTrialDate || updating}
                style={{ flex: 1, padding: '12px', borderRadius: '10px', background: !newTrialDate ? BORDER : AMBER, border: 'none', fontSize: '13px', fontWeight: 700, color: WHITE, cursor: newTrialDate ? 'pointer' : 'not-allowed', fontFamily: 'Space Grotesk, sans-serif' }}>
                {updating ? 'Saving...' : 'Set trial date'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── SUSPEND MODAL ── */}
      {showSuspendModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '16px' }}>
          <div style={{ background: WHITE, borderRadius: '16px', padding: '24px', maxWidth: '360px', width: '100%', fontFamily: 'Space Grotesk, sans-serif' }}>
            <div style={{ fontSize: '32px', textAlign: 'center', marginBottom: '10px' }}>🚫</div>
            <div style={{ fontSize: '15px', fontWeight: 800, color: TEXT, textAlign: 'center', marginBottom: '6px' }}>Suspend this account?</div>
            <div style={{ fontSize: '12px', color: MUTED, textAlign: 'center', marginBottom: '16px' }}>The seller loses all access immediately. Their data is preserved.</div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '11.5px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>Reason <span style={{ fontWeight: 400, color: '#B0B3B8' }}>optional</span></label>
              <textarea value={suspendReason} onChange={e => setSuspendReason(e.target.value)} placeholder="e.g. Payment overdue, Terms violation..." rows={3} style={{ ...inp, resize: 'none', border: '1px solid ' + BORDER }} onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => { setShowSuspendModal(false); setSuspendReason('') }} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid ' + BORDER, background: WHITE, fontSize: '13px', fontWeight: 600, color: MUTED, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>Cancel</button>
              <button onClick={handleSuspend} disabled={updating} style={{ flex: 1, padding: '12px', borderRadius: '10px', background: RED, border: 'none', fontSize: '13px', fontWeight: 700, color: WHITE, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
                {updating ? 'Saving...' : 'Suspend'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD AI CREDITS MODAL ── */}
      {showAddCreditsModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '16px' }}>
          <div style={{ background: WHITE, borderRadius: '16px', padding: '24px', maxWidth: '400px', width: '100%', fontFamily: 'Space Grotesk, sans-serif' }}>
            <div style={{ fontSize: '15px', fontWeight: 800, color: TEXT, marginBottom: '6px' }}>Add AI Studio Credits</div>
            <div style={{ fontSize: '12px', color: MUTED, marginBottom: '16px' }}>Manually add credits for {selected?.shop_name}. Use for international payments, IPN failures, or goodwill credits.</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
              <div>
                <label style={{ fontSize: '11.5px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>Number of credits</label>
                <input type="number" value={addCreditsForm.amount} onChange={e => setAddCreditsForm(f => ({ ...f, amount: e.target.value }))} min="1" max="200" placeholder="e.g. 20" style={{ ...inp, border: '1px solid ' + BORDER, fontSize: '20px', fontWeight: 700, textAlign: 'center' }} onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
                {addCreditsForm.amount && <div style={{ fontSize: '11.5px', color: MUTED, marginTop: '4px', textAlign: 'center' }}>= ৳{Number(addCreditsForm.amount) * 25} value at ৳25/credit</div>}
              </div>
              <div>
                <label style={{ fontSize: '11.5px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>Credit type</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {[{ key: 'paid', label: 'Purchased credits', desc: 'Never expire' }, { key: 'free', label: 'Free credits', desc: 'Resets monthly' }].map(opt => (
                    <button key={opt.key} onClick={() => setAddCreditsForm(f => ({ ...f, type: opt.key }))}
                      style={{ flex: 1, padding: '10px', borderRadius: '9px', border: '2px solid ' + (addCreditsForm.type === opt.key ? BLUE : BORDER), background: addCreditsForm.type === opt.key ? '#EFF6FF' : WHITE, cursor: 'pointer', textAlign: 'center', fontFamily: 'Space Grotesk, sans-serif' }}>
                      <div style={{ fontSize: '12.5px', fontWeight: 700, color: addCreditsForm.type === opt.key ? BLUE : TEXT }}>{opt.label}</div>
                      <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}>{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ fontSize: '11.5px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>Reason <span style={{ fontWeight: 400, color: '#B0B3B8' }}>required for audit</span></label>
                <input type="text" value={addCreditsForm.reason} onChange={e => setAddCreditsForm(f => ({ ...f, reason: e.target.value }))} placeholder="e.g. Paid via Wise $7 — Standard pack — Hamza 11 Jun 2026" style={{ ...inp, border: '1px solid ' + BORDER }} onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => { setShowAddCreditsModal(false); setAddCreditsForm({ amount: '', type: 'paid', reason: '' }) }} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid ' + BORDER, background: WHITE, fontSize: '13px', fontWeight: 600, color: MUTED, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>Cancel</button>
              <button onClick={handleAddCredits} disabled={!addCreditsForm.amount || !addCreditsForm.reason || addingCredits}
                style={{ flex: 2, padding: '12px', borderRadius: '10px', background: !addCreditsForm.amount || !addCreditsForm.reason ? BORDER : GOLD, border: 'none', fontSize: '13px', fontWeight: 700, color: !addCreditsForm.amount || !addCreditsForm.reason ? MUTED : '#0B1F3A', cursor: addCreditsForm.amount && addCreditsForm.reason ? 'pointer' : 'not-allowed', fontFamily: 'Space Grotesk, sans-serif' }}>
                {addingCredits ? 'Adding...' : 'Add ' + (addCreditsForm.amount || '0') + ' credits'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminSellers